/**
 * Web Push: integração com a API de push do backend.
 * Usado para notificações no navegador e no celular (com aba fechada).
 * Requer HTTPS, Service Worker e PushManager.
 */

import api, { openApi } from './api';

const SUBSCRIPTION_ENDPOINT_KEY = 'push_subscription_endpoint';

const SW_PUSH_URL = `${typeof process !== 'undefined' && process.env?.PUBLIC_URL ? process.env.PUBLIC_URL : ''}/sw-push.js`;

/**
 * Converte base64url (ou base64) para Uint8Array para o PushManager.
 * @param {string} base64String
 * @returns {Uint8Array}
 */
export function urlBase64ToUint8Array(base64String) {
  if (!base64String || typeof base64String !== 'string') {
    throw new Error('Chave VAPID inválida');
  }
  const trimmed = base64String.replace(/\s/g, '');
  const padding = '='.repeat((4 - (trimmed.length % 4)) % 4);
  const base64 = (trimmed + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    output[i] = rawData.charCodeAt(i);
  }
  return output;
}

/**
 * Converte ArrayBuffer para base64 (para enviar no JSON ao backend).
 * @param {ArrayBuffer} buffer
 * @returns {string}
 */
function arrayBufferToBase64(buffer) {
  if (!buffer || !buffer.byteLength) return '';
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Verifica se o ambiente suporta Web Push (HTTPS + Service Worker + PushManager).
 * @returns {boolean}
 */
export function isPushSupported() {
  if (typeof window === 'undefined') return false;
  return window.isSecureContext && 'serviceWorker' in navigator && 'PushManager' in window;
}

/**
 * Obtém a chave VAPID pública do backend (GET sem auth, conforme spec).
 * @returns {Promise<string>} base64/base64url da chave pública
 */
export async function getVapidPublicKey() {
  try {
    const { data } = await openApi.get('/push-subscriptions/vapid-public-key');
    const key = data?.vapidPublicKey;
    if (!key || typeof key !== 'string') {
      throw new Error('Resposta inválida: vapidPublicKey não encontrada');
    }
    return key.trim();
  } catch (err) {
    const status = err?.response?.status;
    if (status === 503) {
      throw new Error('Push não disponível no momento. Tente mais tarde.');
    }
    if (err?.message) throw err;
    throw new Error('Não foi possível obter a chave do servidor.');
  }
}

/**
 * Monta o body de subscription no formato esperado pelo backend.
 * @param {PushSubscription} subscription
 * @returns {{ endpoint: string, keys: { p256dh: string, auth: string } }}
 */
function subscriptionToBody(subscription) {
  const p256dh = subscription.getKey('p256dh');
  const auth = subscription.getKey('auth');
  return {
    endpoint: subscription.endpoint,
    keys: {
      p256dh: p256dh ? arrayBufferToBase64(p256dh) : '',
      auth: auth ? arrayBufferToBase64(auth) : '',
    },
  };
}

/**
 * Registra a subscription no backend (POST /push-subscriptions).
 * Requer token de autenticação (api já envia via interceptor).
 * @param {PushSubscription} subscription
 * @returns {Promise<{ id?: number, message?: string }>}
 */
export async function registerSubscription(subscription) {
  const payload = subscriptionToBody(subscription);
  if (!payload.endpoint || !payload.keys?.p256dh || !payload.keys?.auth) {
    throw new Error('Dados da assinatura inválidos. Tente novamente.');
  }
  const body = { subscription: payload };
  try {
    const { data } = await api.post('/push-subscriptions', body);
    return data;
  } catch (err) {
    const status = err?.response?.status;
    if (status === 401) {
      throw new Error('Sessão expirada. Faça login novamente.');
    }
    if (status === 400) {
      throw new Error('Dados inválidos. Verifique se o navegador está atualizado.');
    }
    throw err;
  }
}

/**
 * Remove a subscription no backend (DELETE /push-subscriptions).
 * @param {string} endpoint - subscription.endpoint
 * @returns {Promise<void>}
 */
export async function unregisterSubscription(endpoint) {
  await api.delete('/push-subscriptions', { data: { endpoint } });
}

/**
 * Lista subscriptions do usuário (GET /push-subscriptions).
 * @returns {Promise<Array>}
 */
export async function listSubscriptions() {
  const { data } = await api.get('/push-subscriptions');
  return Array.isArray(data) ? data : [];
}

/**
 * Dispara push para usuários (POST /push-subscriptions/send).
 * @param {{ title: string, body: string, url?: string, data?: object }} payload
 * @param {number[]} [userIds] - se omitido, envia para todos da empresa
 * @returns {Promise<{ message: string, count: number }>}
 */
export async function sendPush(payload, userIds) {
  const body = { payload: { title: payload.title, body: payload.body } };
  if (payload.url) body.payload.url = payload.url;
  if (payload.data) body.payload.data = payload.data;
  if (userIds && userIds.length) body.userIds = userIds;
  const { data } = await api.post('/push-subscriptions/send', body);
  return data;
}

/**
 * Salva o endpoint da subscription atual (para poder desregistrar no logout).
 * @param {string} endpoint
 */
export function saveSubscriptionEndpoint(endpoint) {
  try {
    localStorage.setItem(SUBSCRIPTION_ENDPOINT_KEY, endpoint);
  } catch (_) {
    // localStorage pode estar indisponível
  }
}

/**
 * Recupera o endpoint salvo (para DELETE no logout).
 * @returns {string|null}
 */
export function getSavedSubscriptionEndpoint() {
  try {
    return localStorage.getItem(SUBSCRIPTION_ENDPOINT_KEY);
  } catch (_) {
    return null; // localStorage pode estar indisponível
  }
}

/**
 * Limpa o endpoint salvo (após logout ou desregistro).
 */
export function clearSavedSubscriptionEndpoint() {
  try {
    localStorage.removeItem(SUBSCRIPTION_ENDPOINT_KEY);
  } catch (_) {
    // localStorage pode estar indisponível
  }
}

/**
 * Fluxo completo conforme spec: VAPID → permissão → SW → subscribe → POST.
 * Retorna a subscription em caso de sucesso.
 * @param {string} [vapidBase64] - chave já obtida; se não passar, busca do backend (GET sem auth)
 * @returns {Promise<PushSubscription|null>} subscription ou null se não suportado/negado
 */
export async function requestAndSubscribe(vapidBase64) {
  if (!isPushSupported()) {
    throw new Error(
      'Não disponível neste dispositivo. Use HTTPS e um navegador que suporte notificações.'
    );
  }

  // 1. Obter chave VAPID (GET sem auth)
  const key = vapidBase64 || (await getVapidPublicKey());
  const applicationServerKey = urlBase64ToUint8Array(key);

  // 2. Pedir permissão
  let permission = Notification.permission;
  if (permission === 'denied') {
    throw new Error(
      'Notificações bloqueadas. Habilite nas configurações do navegador para este site.'
    );
  }
  if (permission === 'default') {
    permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      if (permission === 'denied') {
        throw new Error('Notificações bloqueadas.');
      }
      throw new Error('É preciso permitir notificações nas configurações do site/navegador.');
    }
  }

  // 3. Registrar Service Worker e esperar ready (evita loading infinito se SW ainda não estiver pronto)
  const registration = await navigator.serviceWorker.register(SW_PUSH_URL, { scope: '/' });
  await registration.ready;

  // 4 e 5. Subscribe e converter para o formato do backend
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey,
  });

  // 6. Enviar subscription ao backend (POST com auth)
  await registerSubscription(subscription);
  saveSubscriptionEndpoint(subscription.endpoint);
  return subscription;
}

/**
 * Desinscreve no cliente e remove no backend.
 * Usar no logout ou ao desativar notificações.
 * @returns {Promise<void>}
 */
export async function unsubscribeAndUnregister() {
  const endpoint = getSavedSubscriptionEndpoint();
  if (endpoint) {
    try {
      await unregisterSubscription(endpoint);
    } catch (_) {
      // Backend pode já ter removido
    }
    clearSavedSubscriptionEndpoint();
  }

  if (!isPushSupported()) return;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) await subscription.unsubscribe();
  } catch (_) {
    // Ignora falha ao desinscrever no cliente
  }
}
